/**
 * PhotoPickerModal Component
 * Reusable modal for browsing and selecting photos from user's library
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiImage, FiSearch, FiX, FiCheck, FiLoader, FiFilter } from 'react-icons/fi';

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export default function PhotoPickerModal({
  isOpen,
  onClose,
  onSelect,
  excludeIds = [],
  title = 'Add Photos',
  multiSelect = true,
  maxSelections = null,
  albumId = null
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [useAlbumFilter, setUseAlbumFilter] = useState(!!albumId);
  const observerRef = useRef();
  const loadingRef = useRef(false);

  const LIMIT = 50;

  // Load tags for filtering
  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/tags', { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(data => setTags(data || []))
      .catch(() => setTags([]));
  }, [isOpen]);

  // Reset album filter when albumId changes
  useEffect(() => {
    setUseAlbumFilter(!!albumId);
  }, [albumId]);

  // Fetch images, from album if albumId is set and filter active, otherwise from library
  const fetchImages = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const currentOffset = reset ? 0 : offset;

      let newImages = [];

      if (albumId && useAlbumFilter) {
        // Fetch from album, albums endpoint returns all images at once
        if (currentOffset === 0) {
          const res = await fetch(`/api/albums/${albumId}`, { credentials: 'include' });
          if (!res.ok) throw new Error('Failed to load album images');
          const data = await res.json();
          newImages = Array.isArray(data.images) ? data.images : [];
        }
        // Album returns all images, no pagination needed
        if (reset) {
          setImages(newImages);
          setOffset(newImages.length);
        }
        setHasMore(false);
      } else {
        // Fetch from full library
        const params = new URLSearchParams({
          limit: LIMIT,
          offset: currentOffset
        });
        if (selectedTag) params.set('tag', selectedTag);

        const res = await fetch(`/api/images?${params}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load images');

        const data = await res.json();
        newImages = data.images || data || [];

        if (reset) {
          setImages(newImages);
          setOffset(LIMIT);
        } else {
          setImages(prev => [...prev, ...newImages]);
          setOffset(prev => prev + LIMIT);
        }

        setHasMore(newImages.length === LIMIT);
      }
    } catch (error) {
      console.error('Failed to load images:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [offset, selectedTag, albumId, useAlbumFilter]);

  // Initial load and reset on tag/filter change
  useEffect(() => {
    if (!isOpen) return;
    setImages([]);
    setOffset(0);
    setHasMore(true);
    fetchImages(true);
  }, [isOpen, selectedTag, useAlbumFilter]);

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          fetchImages(false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [isOpen, hasMore, loading, fetchImages]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setSelectedTag('');
      setUseAlbumFilter(!!albumId);
      setImages([]);
      setOffset(0);
    }
  }, [isOpen, albumId]);

  // Toggle selection
  const toggleSelect = (imageId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        if (!multiSelect) {
          next.clear();
        }
        if (!maxSelections || next.size < maxSelections) {
          next.add(imageId);
        }
      }
      return next;
    });
  };

  // Confirm selection
  const handleConfirm = () => {
    onSelect(Array.from(selectedIds));
    onClose();
  };

  // Filter by search (client-side for loaded images)
  const filteredImages = images.filter(img => {
    if (excludeIds.includes(img.id)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (img.filename || '').toLowerCase().includes(q) ||
      (img.title || '').toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel photo-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><FiImage /> {title}</h3>
          <button className="modal-close-btn" onClick={onClose}><FiX /></button>
        </div>

        <div className="photo-picker-toolbar">
          {albumId && (
            <div className="photo-picker-source-toggle">
              <button
                className={`source-toggle-btn ${useAlbumFilter ? 'active' : ''}`}
                onClick={() => setUseAlbumFilter(true)}
              >
                Job Uploads
              </button>
              <button
                className={`source-toggle-btn ${!useAlbumFilter ? 'active' : ''}`}
                onClick={() => setUseAlbumFilter(false)}
              >
                All Photos
              </button>
            </div>
          )}

          <div className="photo-picker-search">
            <FiSearch />
            <input
              type="text"
              placeholder="Search photos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {!useAlbumFilter && (
            <div className="photo-picker-filter">
              <FiFilter />
              <select value={selectedTag} onChange={(e) => setSelectedTag(e.target.value)}>
                <option value="">All Photos</option>
                {tags.map(tag => (
                  <option key={tag.id} value={tag.id}>{tag.label || tag.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="photo-picker-count">
            {selectedIds.size} selected
            {maxSelections && ` / ${maxSelections} max`}
          </div>
        </div>

        <div className="photo-picker-grid">
          {filteredImages.length === 0 && !loading ? (
            <div className="photo-picker-empty">
              <FiImage size={48} />
              <p>No photos found</p>
            </div>
          ) : (
            <>
              {filteredImages.map(img => {
                const isSelected = selectedIds.has(img.id);
                // Use the md variant URL - convert filename to jpg extension
                const baseFilename = (img.thumbnail_url || img.filename || '').replace(/\.[^.]+$/, '.jpg');
                const thumbUrl = `/media/variants/md/${baseFilename}`;

                return (
                  <div
                    key={img.id}
                    className={`photo-picker-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleSelect(img.id)}
                  >
                    <img src={thumbUrl} alt={img.title || img.filename} loading="lazy" />
                    <div className="photo-picker-checkbox">
                      {isSelected && <FiCheck />}
                    </div>
                    <div className="photo-picker-info">
                      <span className="photo-picker-date">{formatDate(img.taken_at || img.created_at)}</span>
                    </div>
                  </div>
                );
              })}

              {/* Infinite scroll trigger */}
              <div ref={observerRef} className="photo-picker-loader">
                {loading && <FiLoader className="spin" />}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
          >
            <FiCheck /> Add {selectedIds.size} Photo{selectedIds.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
